/**
 * TypeScript types for the Preferences Wizard frontend
 *
 * These types match the API response shapes from:
 * - POST /api/account/preferences-wizard (start session)
 * - GET /api/account/preferences-wizard (get current session)
 * - POST /api/account/preferences-wizard/respond (send message)
 * - POST /api/account/preferences-wizard/complete (finalize preferences)
 * - DELETE /api/account/preferences-wizard (abandon session)
 */

/**
 * Message role for wizard conversation
 */
export type WizardMessageRole = 'system' | 'assistant' | 'user';

/**
 * Single message in the wizard conversation
 * Matches the message shape returned by GET /api/account/preferences-wizard
 */
export interface WizardMessage {
  id: number;
  role: WizardMessageRole;
  content: string;
  createdAt: string;
}

/**
 * Response from POST /api/account/preferences-wizard (start wizard)
 */
export interface StartWizardResponse {
  success: boolean;
  sessionId?: number;
  conversationUuid?: string;
  openingMessage?: string;
  currentRound?: number;
  maxRounds?: number;
  error?: string;
  /** Error code for specific error types (e.g., 'ACTIVE_SESSION_EXISTS') */
  errorCode?: 'ACTIVE_SESSION_EXISTS' | string;
  /** The existing session ID when errorCode is 'ACTIVE_SESSION_EXISTS' */
  existingSessionId?: number;
}

/**
 * Response from GET /api/account/preferences-wizard (get current session)
 */
export interface GetWizardResponse {
  success: boolean;
  isActive: boolean;
  sessionId?: number;
  conversationUuid?: string;
  messages?: WizardMessage[];
  currentRound?: number;
  maxRounds?: number;
  error?: string;
}

/**
 * Response from POST /api/account/preferences-wizard/respond
 */
export interface WizardRespondResponse {
  success: boolean;
  message?: string;
  isComplete?: boolean;
  currentRound?: number;
  maxRounds?: number;
  suggestedResponses?: string[];
  error?: string;
}

/**
 * Response from POST /api/account/preferences-wizard/complete
 */
export interface WizardCompleteResponse {
  success: boolean;
  preferences?: string;
  sessionId?: number;
  error?: string;
}

/**
 * Response from DELETE /api/account/preferences-wizard
 */
export interface WizardAbandonResponse {
  success: boolean;
  error?: string;
}

/**
 * Complete wizard state for the frontend
 * Aggregates session info and messages for UI rendering
 */
export interface WizardState {
  /** Whether a wizard session is currently active */
  isActive: boolean;
  /** The wizard session ID */
  sessionId: number | null;
  /** The conversation UUID for reference */
  conversationUuid: string | null;
  /** All messages in the conversation (excluding system messages) */
  messages: WizardMessage[];
  /** Current conversation round (0-indexed) */
  currentRound: number;
  /** Maximum rounds configured for this session */
  maxRounds: number;
  /** Whether the wizard has reached its conclusion */
  isComplete: boolean;
  /** Optional quick-reply suggestions from the last AI response */
  suggestedResponses?: string[];
}

/**
 * Request body for starting a new wizard session
 */
export interface StartWizardRequest {
  maxRounds?: number;
}

/**
 * Request body for sending a user response
 */
export interface WizardRespondRequest {
  sessionId?: number;
  message: string;
}

/**
 * Request body for completing the wizard
 */
export interface WizardCompleteRequest {
  sessionId: number;
}

/**
 * Request body for abandoning the wizard
 */
export interface WizardAbandonRequest {
  sessionId: number;
}

/**
 * Conflict state when an active session already exists
 */
export interface ActiveSessionConflict {
  sessionId: number;
}

/**
 * Hook return type for usePreferencesWizard
 */
export interface UsePreferencesWizardReturn {
  /** Current wizard state */
  state: WizardState;
  /** Whether an API request is in progress */
  isLoading: boolean;
  /** Latest error message, if any */
  error: string | null;
  /** Conflict info when trying to start but active session exists */
  activeSessionConflict: ActiveSessionConflict | null;
  /** Start a new wizard session */
  startWizard: (maxRounds?: number) => Promise<boolean>;
  /** Send a user message to the wizard */
  sendMessage: (message: string) => Promise<boolean>;
  /** Complete the wizard and get synthesized preferences */
  completeWizard: () => Promise<string | null>;
  /** Abandon the current wizard session */
  abandonWizard: () => Promise<boolean>;
  /** Refresh the current wizard state from the server */
  refreshState: () => Promise<void>;
  /** Clear any error state */
  clearError: () => void;
  /** Resume the existing session (when activeSessionConflict is set) */
  resumeExistingSession: () => Promise<boolean>;
  /** Abandon existing session and start fresh (when activeSessionConflict is set) */
  startFreshSession: (maxRounds?: number) => Promise<boolean>;
}

/**
 * Default/initial wizard state
 */
export const INITIAL_WIZARD_STATE: WizardState = {
  isActive: false,
  sessionId: null,
  conversationUuid: null,
  messages: [],
  currentRound: 0,
  maxRounds: 10,
  isComplete: false,
  suggestedResponses: undefined,
};
